import {
    AfterContentInit,
    AfterViewInit,
    ApplicationRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    isDevMode,
    NgZone,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    defer,
    fromEvent,
    merge,
    Observable,
    Subject,
    Subscription,
} from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
} from 'rxjs/operators';
import { ControlValueAccessor } from '@angular/forms';
import {
    ActiveDescendantKeyManager,
    FocusMonitor,
    FocusOrigin,
} from '@angular/cdk/a11y';
import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { DOCUMENT } from '@angular/common';
import { DropdownType } from './dropdown-type';
import { DOWN_ARROW, ENTER, TAB, UP_ARROW } from '@angular/cdk/keycodes';
import { POP_EVENT_TYPE } from '@components/pop/pop-event-type';
import { getValueAccessorProvider } from '@core/utils/form';
import { WindowRef } from '@vendor/window/window.component';
import { OptionComponent, OptionSelectionEvent } from '@components/option/option.component';
import { $log } from '@core/utils/logger';
import { ChipCloseEvent } from '@shared/elements/chip/chip-close-event';
import { StrSlicePipe } from '@shared/pipes/string/slice.pipe';
import { ChipComponent } from '@shared/elements/chip/chip.component';
import { isArray, isNullOrUndefined } from '@core/utils/object';

@Component({
    selector: 'ihd-dropdown-base',
    templateUrl: './dropdown-base.component.html',
    //    styleUrls: ['./dropdown-base.component.scss'], NOTE: in global
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [getValueAccessorProvider(DropdownBaseComponent)],
    host: {
        class: 'c-dropdown-base',
        '[attr.tabindex]': 'dynamicTabIndex',
        '(keydown)': 'handleKeydown($event)',
    },
})
export class DropdownBaseComponent
    implements
        OnInit,
        AfterViewInit,
        AfterContentInit,
        OnDestroy,
        ControlValueAccessor {
    @Input()
    placeholder: string;

    @Input()
    dropdownType?: DropdownType = DropdownType.SINGLE;

    /**
     * Use this when the options are objects and not simple strings. The objects key that needs to be used to display
     * the "selected value" must be passed.
     */
    @Input()
    displayKey: string;

    /**
     * Use this when the options are objects and not simple strings. This must be used a callback function which will be called
     * to display the selected option if using "displayKey" is not sufficient and you need more custom control.
     * The function must return a string that will be used to display the value
     */
    @Input()
    displayFn: ((value: any) => string) | null = null;

    /**
     * Use this input to introduce an embedded label, originally used in 'split dropdown' mode
     */
    @Input()
    label: string;

    @Input()
    disabled: boolean;

    /**
     * Used to restrict the maximum length on the displayed selected value
     * Sometimes the selection could be long and you wanted to trim it to specific characters followed by ...
     */
    @Input()
    maxDisplayValueLength: number;

    /**
     * Use this input to introduce a css class for selected item labels, originally used in 'split dropdown' mode
     */
    @Input()
    selectionClass: string = '';

    @Input()
    set tabindex(val: number) {
        this.dynamicTabIndex = val;
    }

    get tabindex(): number {
        return this.dynamicTabIndex;
    }

    @Input()
    popoverWidth?: number;

    @Output()
    inputChange: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    inputClear: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    change: EventEmitter<any> = new EventEmitter<any>();

    @ContentChildren(OptionComponent, { descendants: true })
    set options(val: QueryList<OptionComponent>) {
        this._options = val;
        this.setSelectionByValue();
    }

    get options(): QueryList<OptionComponent> {
        return this._options;
    }

    @ContentChildren(ChipComponent, { descendants: true })
    set selectedChips(val: QueryList<ChipComponent>) {
        this._selectedChips = val;
    }

    get selectedChips(): QueryList<ChipComponent> {
        return this._selectedChips;
    }

    @ViewChild('searchInputField', { static: false })
    public searchInputElement;
    // set searchInputElement(val: ElementRef) {
    //     this._searchInputElement = val;
    //     if (this._searchInputElement) {
    //         this.bindInputEvent();
    //     }
    // }
    //
    // get searchInputElement(): ElementRef {
    //     return this._searchInputElement;
    // }

    @ViewChild('dropdownWrapper', { static: false })
    public dropdownWrapperElement: ElementRef;

    @ViewChild('optionsListPanel', { static: false })
    set optionsListPanel(val: ElementRef) {
        this._optionsListPanel = val;
    }

    get optionsListPanel(): ElementRef {
        return this._optionsListPanel;
    }

    get isFocused(): boolean {
        return this._isFocused;
    }

    set isFocused(focused: boolean) {
        this._isFocused = focused;
    }

    get activeOption(): OptionComponent | null {
        if (this.keyManager) {
            return this.keyManager.activeItem;
        }

        return null;
    }

    get panelClosingActions() {
        return merge(
            this.optionSelections
            // this.keyManager.tabOut.pipe(filter(() => true )),
        ).pipe(
            // Normalize the output so we return a consistent type.
            map((event) =>
                event instanceof OptionSelectionEvent ? event : null
            )
        );
    }

    public onChange: any;
    public onTouched: any;
    public keyManager: ActiveDescendantKeyManager<OptionComponent>;

    set searchInput(val: string) {
        this._searchInput = val;
        this.displayValue = !this.maxDisplayValueLength
            ? this._searchInput
            : this.strSlicePipe.transform(
                  this._searchInput,
                  this.maxDisplayValueLength
              );
        if (
            this.dropdownType === DropdownType.SINGLE &&
            this.displayValue?.length > 0
        ) {
            this.searchInputElement.nativeElement.value = val;
        }
        this.cdr.detectChanges();
    }

    get searchInput(): string {
        return this._searchInput;
    }

    /**
     * Display value is the same as searchInput but could be a trimmed version if maxDisplayValueLength was provided
     */
    public displayValue: string = '';
    public selectedValue: string = '';
    /**
     * To be used in template since it is repetitive in a few places
     */
    public selectedCount: number = 0;

    public optionsMap: Map<string | number, boolean> = new Map();
    public selectionModel: SelectionModel<string | number>;

    /**
     * Dynamically sets the tabindex of the component depending on the focus
     * In case of single/multi when the focus is on input the tabindex of the component is set to -1 for supporting shift+tab
     */
    public dynamicTabIndex: number = 0;

    readonly optionSelections: Observable<OptionSelectionEvent> = defer(() => {
        if (this.options) {
            // this.ngZone.run(() => {
            const selected: (string | number)[] = this.selectionModel.selected;

            this.options.map((option: OptionComponent) => {
                // const isSelected: boolean = this.optionsMap.has(option.id) ? this.optionsMap.get(option.id) : false;
                if (selected.includes(option.id)) {
                    option.select();
                } else {
                    option.deselect();
                }
            });
            // this.appRef.tick();
            // });
            return merge(
                ...this.options.map((option) => option.onSelectionChange)
            );
        }

        // If there are any subscribers before `ngAfterViewInit`, the `autocomplete` will be undefined.
        // Return a stream that we'll replace with the real one once everything is in place.
        return this.ngZone.onStable.asObservable().pipe(
            take(1),
            switchMap(() => this.optionSelections)
        );
    }) as Observable<OptionSelectionEvent>;

    readonly selectedChipsChanges: Observable<ChipCloseEvent> = defer(() => {
        if (this.selectedChips) {
            const ids = this.selectedChips.map((a) => a.id);

            // this.ngZone.run(() => {
            //     const selected: (string|number)[] = this.selectionModel.selected;
            //
            //     this.options.map((option: OptionComponent) => {
            //         // const isSelected: boolean = this.optionsMap.has(option.id) ? this.optionsMap.get(option.id) : false;
            //         if (selected.includes(option.id)) {
            //             option.select()
            //         } else {
            //             option.deselect();
            //         }
            //     });
            // });

            return merge(...this.selectedChips.map((chip) => chip.onClose));
        }

        // If there are any subscribers before `ngAfterViewInit`, the `autocomplete` will be undefined.
        // Return a stream that we'll replace with the real one once everything is in place.
        return this.ngZone.onStable.asObservable().pipe(
            take(1),
            switchMap(() => this.selectedChips)
        );
    }) as Observable<ChipCloseEvent>;

    public isInputHidden: boolean = true;
    public isShowingPopover: boolean = false;
    public DropdownType = DropdownType;

    private canOpenOnNextFocus = true;
    private nativeElement: HTMLElement;
    private _searchInputElement: ElementRef;
    private _optionsListPanel: ElementRef;
    private _options: QueryList<OptionComponent>;
    private _selectedChips: QueryList<ChipComponent>;
    private _isFocused: boolean;
    private destroyed$: Subject<void> = new Subject();
    private closingSubscription: Subscription;
    private chipsCloseSubscription: Subscription;
    private inputValue: any;
    private _searchInput: string;

    private applyingChanges: boolean = false;
    private blurTimeOut: any;
    private focusTimeout: any;

    constructor(
        private focusMonitor: FocusMonitor,
        private elementRef: ElementRef,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(DOCUMENT) private document: any,
        public windowRef: WindowRef,
        private ngZone: NgZone,
        private appRef: ApplicationRef,
        private strSlicePipe: StrSlicePipe
    ) {
        this.nativeElement = this.elementRef.nativeElement;

        this.isInputHidden = !!(
            !this.isShowingPopover &&
            this.dropdownType === DropdownType.MULTIPLE &&
            this.selectionModel?.selected.length
        );

        // TODO: come back to this
        // this.ngZone.runOutsideAngular(() => {
        //     this.windowRef.nativeWindow.addEventListener('blur', this.windowBlurHandler);
        // });
    }

    ngOnInit() {
        this.selectionModel = new SelectionModel<string | number>(
            this.dropdownType === DropdownType.MULTIPLE
        );

        // open up the popover again when the user clicks inside the textbox or the div (dropdown)
        // when it is already in focused state
        fromEvent(this.nativeElement, 'click')
            .pipe(
                filter((event: Event) => {
                    const isClicking: boolean =
                        this.isFocused &&
                        !this.isShowingPopover &&
                        this.dropdownType !== DropdownType.SINGLE;
                    return isClicking;
                }),
                takeUntil(this.destroyed$)
            )
            .subscribe((event: Event) => {
                this.showList();
            });

        this.focusMonitor
            .monitor(this.nativeElement, false)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((origin: FocusOrigin) => {
                //this.ngZone.run(() => {
                if (this.blurTimeOut) {
                    clearTimeout(this.blurTimeOut);
                }
                if (origin) {
                    if (!this.isFocused) {
                        this.onFocus(origin);
                    }
                } else {
                    this.onBlur();
                    this.dynamicTabIndex = 0;
                    this.cdr.detectChanges();

                    if (this.isShowingPopover) {
                        this.blurTimeOut = setTimeout(() => {
                            if (!this.isFocused && !this.applyingChanges) {
                                // if (!this.selectedValue) {
                                if (
                                    this.dropdownType === DropdownType.SINGLE ||
                                    this.dropdownType === DropdownType.MULTIPLE
                                ) {
                                    if (this.options?.length) {
                                        if (this.searchInput?.length) {
                                            const matchedOption: OptionComponent = this.options.find(
                                                (option) => {
                                                    const optionDisplayValue: string = option.value[
                                                        this.displayKey
                                                    ].toLowerCase();
                                                    return (
                                                        optionDisplayValue ===
                                                        this.searchInput
                                                            .trim()
                                                            .toLowerCase()
                                                    );
                                                }
                                            );

                                            if (
                                                matchedOption &&
                                                !matchedOption?.isSelected
                                            ) {
                                                if (
                                                    this.dropdownType ===
                                                    DropdownType.SINGLE
                                                ) {
                                                    this.writeValue(
                                                        matchedOption.value
                                                    );
                                                    this.onChange(
                                                        matchedOption.value
                                                    );
                                                } else if (
                                                    this.dropdownType ===
                                                    DropdownType.MULTIPLE
                                                ) {
                                                    this.selectionModel.select(
                                                        matchedOption.id
                                                    );
                                                    let valuesToEmit: any[] = this.selectionModel.selected.map(
                                                        (tag) => {
                                                            return {
                                                                id: tag,
                                                                label: tag,
                                                            };
                                                        }
                                                    );
                                                    this.writeValue(
                                                        valuesToEmit
                                                    );
                                                    this.onChange(valuesToEmit);
                                                    this.change.emit(
                                                        valuesToEmit
                                                    );
                                                }
                                                this.cdr.detectChanges();
                                                this.clear();
                                            }
                                        } else {
                                            // this.writeValue(null);
                                            // this.onChange(null);
                                        }
                                    } else {
                                        // when no custom entries are allowed, the options array would be empty
                                        // if (this.searchInput.length) {
                                        // } else {
                                        //     this.writeValue(null);
                                        //     this.onChange(null);
                                        // }
                                    }
                                }

                                // }
                                // this.blurValue()
                                // on blurring we want to close the popover but the blur event also happens when the user selects an option
                                // so we are just delaying to make sure that is not case
                                if (this.isShowingPopover) {
                                    this.hideList();
                                    this.cdr.detectChanges();
                                }
                                this.appRef.tick();
                            }
                        }, 150);
                    }
                }
                // });
            });
    }

    ngAfterViewInit() {
        if (this.searchInputElement) {
            this.focusMonitor
                .monitor(this.searchInputElement)
                .pipe(takeUntil(this.destroyed$))
                .subscribe((origin: FocusOrigin) => {
                    // this.ngZone.run(() => {
                    if (origin === 'mouse' || origin === 'program') {
                        this.onFocus(origin);
                    }
                    // });
                });
        }

        if (
            this.dropdownType !== DropdownType.DROPDOWN &&
            this.dropdownType !== DropdownType.SPLIT_DROPDOWN
        ) {
            fromEvent<InputEvent>(
                this.searchInputElement.nativeElement,
                'input'
            )
                .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
                .subscribe((event: InputEvent) => {
                    this.searchInput = (event.target as any).value;
                    this.inputChange.next(this.searchInput);
                    if (!this.isShowingPopover) {
                        this.showList();
                    }
                    this.cdr.markForCheck();
                });
        }
    }

    ngAfterContentInit() {
        this.keyManager = new ActiveDescendantKeyManager<OptionComponent>(
            this.options
        ).withWrap();

        if (this.inputValue) {
            this.setSelectionByValue();
        }

        this.selectionModel.changed
            .pipe(takeUntil(this.destroyed$))
            .subscribe((change: SelectionChange<number | string>) => {
                this.selectedCount = (
                    this.selectionModel.selected || []
                ).length;

                this.cdr.detectChanges();
                // this.appRef.tick();
                // });
                // this.appRef.tick();
                // });

                // this.ngZone.run(() => {
                //     change.added.forEach((added: number|string) => {
                //         $log.debug('added', added);
                //         this.options.forEach((option: OptionComponent) => {
                //             if (option.id === added) {
                //                 $log.debug('get hers..');
                //                 option.select();
                //             }
                //         })
                //     });
                //
                //     change.removed.forEach((removed: number|string) => {
                //         this.options.forEach((option: OptionComponent) => {
                //             if (option.id === removed) {
                //                 option.deselect();
                //             }
                //         })
                //     });
                // });
            });

        merge(
            this.selectedChips.changes,
            this.ngZone.onStable.asObservable().pipe(take(1))
        )
            .pipe(takeUntil(this.destroyed$))
            .subscribe((changes) => {
                if (this.chipsCloseSubscription) {
                    this.chipsCloseSubscription.unsubscribe();
                }

                this.chipsCloseSubscription = this.selectedChipsChanges
                    .pipe(take(1))
                    .subscribe((close: ChipCloseEvent) => {
                        this.selectionModel.deselect(close.source.id);
                        let valuesToEmit: any[] = this.selectionModel.selected.map(
                            (tag) => {
                                return { id: tag, label: tag };
                            }
                        );

                        this.writeValue(valuesToEmit);
                        if (this.onChange) {
                            this.onChange(valuesToEmit);
                        }
                        this.change.emit(valuesToEmit);
                    });

                // Update input element / placeholder
                this.isInputHidden = !!(
                    !this.isShowingPopover &&
                    this.dropdownType === DropdownType.MULTIPLE &&
                    this.selectionModel.selected?.length
                );

                this.cdr.detectChanges();
            });
    }

    ngOnDestroy() {
        clearTimeout(this.focusTimeout);
        clearTimeout(this.blurTimeOut);
        this.windowRef.nativeWindow.removeEventListener(
            'blur',
            this.windowBlurHandler
        );

        this.focusMonitor.stopMonitoring(this.nativeElement);
        this.destroyed$.next();
    }

    clear() {
        if (this.searchInputElement) {
            this._searchInput = null;
            this.searchInputElement.nativeElement.value = null;
        }
        this.inputClear.emit();
    }

    showList() {
        if (!this.isShowingPopover && !this.disabled) {
            this.isShowingPopover = true;
            this.cdr.markForCheck();

            const firstStable = this.ngZone.onStable
                .asObservable()
                .pipe(take(1));
            const optionChanges = this.options.changes.pipe(
                // Defer emitting to the stream until the next tick, because changing
                // bindings in here will cause "changed after checked" errors.
                delay(0)
            );

            // just as a safety measure, unsubscribe here as well to ensure there are not multiple subscriptions in parallel
            if (this.closingSubscription) {
                this.closingSubscription.unsubscribe();
            }

            // When the zone is stable initially, and when the option list changes...
            this.closingSubscription = merge(firstStable, optionChanges)
                .pipe(
                    switchMap(() => {
                        this.resetActiveItem();
                        return this.panelClosingActions;
                    }),
                    take(1)
                )
                .subscribe((option: OptionSelectionEvent) => {
                    let changedValue;
                    this.applyingChanges = true;
                    if (option.source.isSelected) {
                        if (this.dropdownType === DropdownType.MULTIPLE) {
                            this.selectionModel.select(option.source.id);
                            let valuesToEmit: any[] = this.selectionModel.selected.map(
                                (tag) => {
                                    return { id: tag, label: tag };
                                }
                            );

                            changedValue = valuesToEmit;
                        } else {
                            this.deselectOtherOptions(option.source);
                            this.selectionModel.select(option.source.id);
                            this.inputValue = option.source.value;
                            this.setDisplayValue(option.source.value, true);
                            changedValue = option.source.value;
                        }

                        // this.setFocus(true);
                        // this.nativeElement.focus();
                        this.focusMonitor.focusVia(
                            this.nativeElement,
                            'program'
                        );
                        // this.searchInputElement.setSelectedValue(this.selectedValue);
                    } else {
                        this.selectionModel.deselect(option.source.id);
                        if (this.dropdownType === DropdownType.MULTIPLE) {
                            // let valuesToEmit: any[] = this.options
                            //     .filter((optionValue: OptionComponent) =>
                            //         this.selectionModel.selected.includes(
                            //             optionValue.id
                            //         )
                            //     )
                            //     .map(
                            //         (optionValue: OptionComponent) =>
                            //             optionValue.value
                            //     );

                            let valuesToEmit: any[] = this.selectionModel.selected.map(
                                (tag) => {
                                    return { id: tag, label: tag };
                                }
                            );
                            changedValue = valuesToEmit;
                        } else {
                            // this.setDisplayValue(option.source.value);
                            changedValue = null;
                        }
                    }
                    this.clear();
                    this.writeValue(changedValue);
                    if (this.onChange) {
                        this.onChange(changedValue);
                    }
                    this.change.emit(changedValue);

                    this.hideList();
                    this.applyingChanges = false;
                    this.cdr.markForCheck();
                });
        }
    }

    hideList() {
        if (this.closingSubscription) {
            this.closingSubscription.unsubscribe();
        }
        this.isShowingPopover = false;
        this.cdr.markForCheck();
    }

    deselectOtherOptions(skip: OptionComponent) {
        this.options.forEach((option: OptionComponent) => {
            if (option !== skip && option.isSelected) {
                option.deselect();
            }
        });

        // this.optionsMap.forEach((value: boolean, key: number|string) => {
        //     // go through all options other than skip in the map and set them to false
        //     // since this.options above could be just the filtered list so it may not contain all items
        //     if (key !== skip.id && this.optionsMap.get(key)) {
        //         this.optionsMap.set(key, false);
        //     }
        // });
    }

    onFocus(origin?: FocusOrigin) {
        if (this.canOpenOnNextFocus) {
            this.isFocused = true;

            if (origin !== 'program') {
                this.showList();
            }

            if (
                this.dropdownType === DropdownType.SINGLE ||
                this.dropdownType === DropdownType.MULTIPLE
            ) {
                if (this.searchInputElement) {
                    if (
                        this.windowRef.nativeWindow.document.activeElement !==
                            this.searchInputElement.nativeElement &&
                        (origin === 'mouse' || origin === 'program')
                    ) {
                        // Force the focus into the search input
                        this.isInputHidden = false;
                        clearTimeout(this.focusTimeout);
                        this.focusTimeout = setTimeout(() => {
                            this.focusMonitor.focusVia(
                                this.searchInputElement.nativeElement,
                                'program'
                            );
                            this.dynamicTabIndex = -1;
                            this.appRef.tick();
                        }, 200);
                    } else {
                        this.isInputHidden = !!(
                            !this.isShowingPopover &&
                            this.dropdownType === DropdownType.MULTIPLE &&
                            this.selectionModel.selected.length
                        );
                    }
                }
            }

            this.cdr.detectChanges();
        } else {
            this.canOpenOnNextFocus = true;
        }
    }

    onBlur() {
        this.isFocused = false;
        this.cdr.detectChanges();
    }

    setFocus(focus: boolean) {
        this.isFocused = focus;
        this.cdr.detectChanges();
    }

    writeValue(value: any) {
        this.inputValue = value;
        setTimeout(() => {
            this.setDisplayValue(value);
            this.appRef.tick();
        });
    }

    /**
     * Set the function to be called
     * when the control receives a change event.
     */
    registerOnChange(fn: (_: any) => {}) {
        this.onChange = fn;
    }

    /**
     * Set the function to be called
     * when the control receives a touch event.
     */
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    handleKeydown(event: KeyboardEvent) {
        const keyCode = event.keyCode;

        if (this.activeOption && keyCode === ENTER && this.isShowingPopover) {
            this.activeOption.selectViaClick(true);
            this.resetActiveItem();
            event.preventDefault();
            this.clear();
        } else if (
            keyCode === TAB &&
            this.options?.length &&
            this.isShowingPopover &&
            this.searchInput?.length
        ) {
            this.options.first.selectViaClick(true);
            this.resetActiveItem();
            event.preventDefault();
            this.clear();
        } else {
            const prevActiveItem = this.keyManager.activeItem;
            const isArrowKey = keyCode === UP_ARROW || keyCode === DOWN_ARROW;

            if (this.isShowingPopover || keyCode === TAB) {
                this.keyManager.onKeydown(event);
            }
            // else if (isArrowKey && this._canOpen()) {
            //     this.openPanel();
            // }

            // if (isArrowKey || this.autocomplete._keyManager.activeItem !== prevActiveItem) {
            //     this._scrollToOption();
            // }

            if (isArrowKey || this.keyManager.activeItem !== prevActiveItem) {
                this.scrollToOption();
            }
        }
    }

    onPopoverClose(event: { type: POP_EVENT_TYPE }) {
        this.hideList();
        this.cdr.markForCheck();
    }

    private setDisplayValue(value: any, viaInteraction: boolean = false) {
        if (
            this.dropdownType === DropdownType.SINGLE ||
            this.dropdownType === DropdownType.DROPDOWN ||
            this.dropdownType === DropdownType.SPLIT_DROPDOWN
        ) {
            let displayValue: string = value;

            if (value) {
                if (this.displayKey) {
                    displayValue = value[this.displayKey];
                } else if (this.displayFn && value) {
                    displayValue = this.displayFn(value);
                }

                if (
                    isDevMode() &&
                    typeof value === 'object' &&
                    value &&
                    !value.hasOwnProperty(this.displayKey) &&
                    !this.displayFn
                ) {
                    $log.error(
                        'Please make sure to provide a valid displayKey or displayFn input to display the selected value'
                    );
                }
            }

            // this.selectedValue = displayValue != null ? displayValue : '';
            this.searchInput = displayValue != null ? displayValue : '';
            if (this.dropdownType === DropdownType.SINGLE) {
                this.inputChange.next(this.searchInput);
            }
        }

        if (this.options && !viaInteraction) {
            this.setSelectionByValue();
        }

        this.cdr.detectChanges();
    }

    private setSelectionByValue() {
        if (this.options?.length) {
            if (this.inputValue) {
                const inputValues: any[] = isArray(this.inputValue)
                    ? this.inputValue
                    : [this.inputValue];

                this.options.map((option: OptionComponent) => {
                    if (isDevMode() && isNullOrUndefined(option.id)) {
                        $log.error('Each ihd-option must be provided id input');
                    }

                    const isSame: boolean = inputValues.find((input: any) =>
                        option.compare(input)
                    );

                    if (isSame) {
                        this.selectionModel.select(option.id);
                    } else {
                        this.selectionModel.deselect(option.id);
                    }
                });
            } else {
                this.selectionModel.clear();
            }
        }
    }

    // private selectValue(value: any): OptionComponent | undefined {
    //     // const correspondingOption: OptionComponent|null = this.options.find((option: OptionComponent) => {
    //     //
    //     //     // try {
    //     //     //     // Treat null as a special reset value.
    //     //     //     return option.value != null && this._compareWith(option.value,  value);
    //     //     // } catch (error) {
    //     //     //     if (isDevMode()) {
    //     //     //         // Notify developers of errors in their comparator.
    //     //     //         console.warn(error);
    //     //     //     }
    //     //     //     return false;
    //     //     // }
    //     // });
    //     //
    //     // if (correspondingOption) {
    //     //     this.selectionModel.select(correspondingOption.id);
    //     // }
    //     //
    //     // return correspondingOption;
    // }

    private resetActiveItem(): void {
        this.keyManager.setActiveItem(0);
    }

    private scrollToOption(): void {
        const index = this.keyManager.activeItemIndex || 0;
        // const labelCount = _countGroupLabelsBeforeOption(index,
        //   this.autocomplete.options, this.autocomplete.optionGroups);

        const newScrollPosition = this.getOptionScrollPosition(
            index,
            42,
            this.optionsListPanel
                ? this.optionsListPanel.nativeElement.scrollTop
                : 0,
            250
        );

        if (this.optionsListPanel) {
            this.optionsListPanel.nativeElement.scrollTop = newScrollPosition;
        }
    }

    private getOptionScrollPosition(
        optionIndex: number,
        optionHeight: number,
        currentScrollPosition: number,
        panelHeight: number
    ): number {
        const optionOffset = optionIndex * optionHeight;

        if (optionOffset < currentScrollPosition) {
            return optionOffset;
        }

        if (optionOffset + optionHeight > currentScrollPosition + panelHeight) {
            return Math.max(0, optionOffset - panelHeight + optionHeight);
        }

        return currentScrollPosition;
    }

    /**
     * Event handler for when the window is blurred. Needs to be an
     * arrow function in order to preserve the context.
     */
    private windowBlurHandler = () => {
        // If the user blurred the window while the autocomplete is focused, it means that it'll be
        // refocused when they come back. In this case we want to skip the first focus event, if the
        // pane was closed, in order to avoid reopening it unintentionally.
        this.canOpenOnNextFocus = this.isFocused || this.isShowingPopover;
        // $log.debug('this.canOpenOnNextFocus', this.canOpenOnNextFocus);
        // $log.debug('this.document.activeElement', this.document.activeElement);
    };
}
